'use client';

import Link from 'next/link';
import { Eye, EyeOff, Mail, Lock, User, MapPin } from 'lucide-react';
import { useState } from 'react';
import Image from 'next/image';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { registerSchema } from '@/lib/validations/auth';
// import SearchAutocomplete from "../../components/SearchAutocomplete"

import { Form, FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { useRouter, useSearchParams } from 'next/navigation';
import { handleApiErrors } from '@/lib/api-error-utils';
import { showToast } from '@/lib/toast';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Phone } from 'lucide-react';
import { LogoImage, LogoRegister, LogoSlogan, LogoText } from '@/components/common';

import { PhoneNumberUtil } from 'google-libphonenumber';

import Script from 'next/script';
import { getRecaptchaToken } from '@/lib/recaptcha';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import CommonNav from '@/components/layouts/CommonNav';
import SearchAutocomplete from '@/components/SearchAutocomplete';

export default function Register({ dialCodes }) {
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const phoneUtil = PhoneNumberUtil.getInstance();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl') || '/signin';

  const form = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      city: '',
      countryCode: '1',
      phoneNumber: '',
      fullPhoneNumber: '',
      dailyInspiration: true,
    },
  });

  const onSubmit = async (formData) => {
    const token = await getRecaptchaToken('signup');
    const { fullPhoneNumber } = formData;
    let payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      city: formData.city,
      email: formData.email,
      password: formData.password,
      recaptchaToken: token,
      recaptchaAction: 'signup',
      dailyInspiration: formData.dailyInspiration,
    };

    if (fullPhoneNumber) {
      const number = phoneUtil.parse(fullPhoneNumber);
      // Check if valid number for region
      if (!phoneUtil.isValidNumber(number)) {
        form.setError('fullPhoneNumber', {
          type: 'manual',
          message: 'Phone number is invalid for the selected country.',
        });

        return;
      }

      payload = {
        ...payload,
        phoneNumber: number.getNationalNumber().toString(),
        countryCode: number.getCountryCode().toString(),
      };
    }

    try {
      delete formData.confirmPassword;
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        form.reset();
        showToast(data?.message || 'Success', 'success');
        if (callbackUrl) {
          router.push(`/signin?callbackUrl=${callbackUrl}`);
          return;
        }

        router.push('/signin');
      } else {
        if (response.status === 400 || response.status === 409) {
          handleApiErrors(form, data.errors);
          return;
        }
        showToast(data?.message || 'An error occurred during signin', 'error');
        return;
      }
      return;
    } catch (error) {
      showToast(error?.message || 'An error occurred during signin', 'error');
    }
  };

  const handleGoogleLogin = () => {
    router.push(`${process.env.NEXT_PUBLIC_API_URL}/auth/google`);
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-gray-50 p-4"
      style={{
        background: 'linear-gradient(180deg, #678CCD 0%, #FFFFFF 100%)',
      }}
    >
      <div className="w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-4 flex justify-center sm:mb-5">
          <Link href="/" className={`flex flex-col items-center justify-center`}>
            <div className="flex">
              <LogoImage />
              {/* <LogoText className="text-4xl" /> */}
              {/* <LogoRegister className="text-xl font-semibold" /> */}
            </div>
            <LogoSlogan className="me-4 text-center text-[.8rem]" />
          </Link>
        </div>
        <div className="mb-3">
          <CommonNav />
        </div>

        <div className="mb-6 text-start sm:mb-8">
          <h1 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl md:text-3xl">
            Create your account
          </h1>
          <p className="text-dark-gray text-sm sm:text-base">
            <span className="me-1">Already have an account?</span>
            <Link href="/signin" className="text-dark-blue font-medium hover:underline">
              Sign In
            </Link>
          </p>
        </div>

        <Form {...form}>
          {/* <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit(onSubmit)}> */}
          <form className="space-y-4 sm:space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* First Name */}
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="">
                    <div className="relative h-12">
                      <User className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-black sm:h-5 sm:w-5" />
                      <FormControl>
                        <Input
                          placeholder="First Name"
                          className="h-11 rounded-xl border-gray-200 bg-gray-50 pl-10 sm:h-12"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage className="text-xs text-red-600 sm:text-sm" />
                  </FormItem>
                )}
              />

              {/* Last Name */}
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="">
                    <div className="relative h-13">
                      <User className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-black sm:h-5 sm:w-5" />
                      <FormControl>
                        <Input
                          placeholder="Last Name"
                          className="h-11 rounded-xl border-gray-200 bg-gray-50 pl-10 sm:h-12"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage className="text-xs text-red-600 sm:text-sm" />
                  </FormItem>
                )}
              />
            </div>

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <div className="relative">
                    <Mail className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-black sm:h-5 sm:w-5" />
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Email"
                        className="h-11 rounded-xl border-gray-200 bg-gray-50 pl-10 sm:h-12"
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage className="text-xs text-red-600 sm:text-sm" />
                </FormItem>
              )}
            />

            {/* City */}
            <FormField
              control={form.control}
              name="city"
              render={() => (
                <FormItem className="relative space-y-2">
                  <div>
                    <SearchAutocomplete form={form} />
                  </div>
                </FormItem>
              )}
            />

            {/* Country */}
            <FormField
              control={form.control}
              name="fullPhoneNumber"
              render={({ field }) => (
                <FormItem className="">
                  <div className="relative">
                    <FormControl>
                      <PhoneInput
                        {...field}
                        country={'us'}
                        value={field.value}
                        onChange={(value, country) => {
                          field.onChange(value && `+${value}`);
                          const countryCode = `+${country.dialCode}`;
                          form.setValue('countryCode', countryCode);
                        }}
                        inputClass="!pl-10 !h-11 sm:!h-12 !w-full !bg-gray-50 !border-gray-200 !rounded-xl !text-sm"
                        buttonClass="!bg-gray-50 hover:!bg-gray-50 focus:!bg-gray-50 !border-gray-200 !rounded-l-xl"
                        containerClass="!w-full"
                        placeholder="Phone (optional)"
                        inputProps={{
                          name: 'phoneNumber',
                          required: false,
                        }}
                        preferredCountries={['us']}
                      />
                    </FormControl>
                  </div>
                  <FormMessage className="text-xs text-red-600 sm:text-sm" />
                </FormItem>
              )}
            />

            {/* Password */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <div className="relative">
                    <Lock className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-black sm:h-5 sm:w-5" />
                    <FormControl>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        className="h-11 rounded-xl border-gray-200 bg-gray-50 pr-10 pl-10 sm:h-12"
                        {...field}
                      />
                    </FormControl>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-1/2 right-3 -translate-y-1/2 transform text-black"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 cursor-pointer sm:h-5 sm:w-5" />
                      ) : (
                        <Eye className="h-4 w-4 cursor-pointer sm:h-5 sm:w-5" />
                      )}
                    </button>
                  </div>
                  <FormMessage className="text-xs text-red-600 sm:text-sm" />
                </FormItem>
              )}
            />

            {/* Confirm Password */}
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <div className="relative">
                    <Lock className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-black sm:h-5 sm:w-5" />
                    <FormControl>
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm Password"
                        className="h-11 rounded-xl border-gray-200 bg-gray-50 pr-10 pl-10 sm:h-12"
                        {...field}
                      />
                    </FormControl>
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute top-1/2 right-3 -translate-y-1/2 transform text-black"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 cursor-pointer sm:h-5 sm:w-5" />
                      ) : (
                        <Eye className="h-4 w-4 cursor-pointer sm:h-5 sm:w-5" />
                      )}
                    </button>
                  </div>
                  <FormMessage className="text-xs text-red-600 sm:text-sm" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dailyInspiration"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Label className="hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 has-[[aria-checked=true]]:bg-blue-50 dark:has-[[aria-checked=true]]:border-blue-900 dark:has-[[aria-checked=true]]:bg-blue-950">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white dark:data-[state=checked]:border-blue-700 dark:data-[state=checked]:bg-blue-700"
                      />
                      <div className="grid gap-1.5 font-normal">
                        <p className="text-sm leading-none font-medium">Get daily inspiration</p>
                        <p className="text-muted-foreground text-[.65rem]">
                          We’ll send you inspiring videos each day. You can stop emails whenever you
                          like.
                        </p>
                      </div>
                    </Label>
                  </FormControl>
                  <FormMessage className="text-xs text-red-600 sm:text-sm" />
                </FormItem>
              )}
            />

            {/* Submit */}
            <Button
              type="submit"
              className="bg-dark-blue h-11 w-full cursor-pointer rounded-4xl font-medium text-white hover:bg-blue-700 sm:h-12"
            >
              {form.formState.isSubmitting ? (
                <>
                  Signing up...
                  <Loader2 className="ml-2 size-6 animate-spin" />
                </>
              ) : (
                'Sign Up'
              )}
            </Button>

            {/* Divider */}
            <div className="relative">
              <Separator className="my-4 sm:my-6" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="bg-white px-2 text-xs text-gray-500 sm:text-sm">OR</span>
              </div>
            </div>

            {/* Social */}
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="bg-muted-brown h-11 w-full rounded-4xl border-gray-200 font-medium hover:bg-gray-100 sm:h-12"
                disabled={form.formState.isSubmitting}
                onClick={handleGoogleLogin}
              >
                <Image
                  src="/assets/icons/google.png"
                  width={18}
                  height={18}
                  alt="Google"
                  className="mr-3 h-4 w-4 sm:h-5 sm:w-5"
                />
                Continue with Google
              </Button>

              {/* <Button
                variant="outline"
                className="w-full h-11 sm:h-12 bg-muted-brown border-gray-200 rounded-4xl font-medium hover:bg-gray-100"
                disabled
              >
                <Image
                  src="/assets/icons/apple.png"
                  width={18}
                  height={18}
                  alt="Apple"
                  className="mr-3 w-4 sm:w-5 h-4 sm:h-5"
                />
                Continue with Apple
              </Button> */}
              <div className="mt-5">
                <p className="text-center text-xs text-gray-500 sm:text-sm">
                  By continuing, you agree to our
                </p>
                <p className="text-center text-xs text-gray-500 sm:text-sm">
                  <Link href="/pages/terms-of-use" className="text-black">
                    Term of Use
                  </Link>{' '}
                  and{' '}
                  <Link href="/pages/privacy-policy" target="_blank" className="text-black">
                    Privacy Policy.
                  </Link>
                </p>
              </div>
            </div>
          </form>
        </Form>
      </div>

      <Script
        id="recaptcha-v3"
        src={`https://www.google.com/recaptcha/api.js?render=${siteKey}`}
        strategy="afterInteractive"
      />
    </div>
  );
}
